import { JobEventName } from '@treadinc/horizon-api-spec';
import { Dayjs } from 'dayjs';
import { useMemo } from 'react';

import type { JobTripEvent } from '~hooks/useJob/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';

import { getCycleSegmentType, SegmentTypes } from './useGetCycleSegments';

export interface PauseInterval {
  start: Dayjs;
  end: Dayjs;
  type: string;
  isOpen?: boolean;
}

/**
 * Returns the segment type active at `time` by mirroring useGetCycleSegments'
 * single-ping filter: if the last ping before `time` is a lone state-transition
 * ping (different type from the one before it), it would be invisible on the
 * cycle timeline, so we skip it and use the preceding ping's type instead.
 */
const findSegmentTypeBefore = (pings: LocationHistoryMinimal[], time: Dayjs): string => {
  let lastIdx = -1;
  for (let i = pings.length - 1; i >= 0; i--) {
    if (pings[i].occurredAt.valueOf() <= time.valueOf()) {
      lastIdx = i;
      break;
    }
  }

  if (lastIdx === -1) {
    return pings.length > 0 ? getCycleSegmentType(pings[0]) : SegmentTypes.LOGGING;
  }

  const lastType = getCycleSegmentType(pings[lastIdx]);

  if (lastIdx > 0 && getCycleSegmentType(pings[lastIdx - 1]) !== lastType) {
    return getCycleSegmentType(pings[lastIdx - 1]);
  }

  return lastType;
};

const usePauseSegments = (
  events: JobTripEvent[],
  pings: LocationHistoryMinimal[],
): PauseInterval[] =>
  useMemo(() => {
    const sorted = [...events].sort(
      (a, b) => a.occurredAt.valueOf() - b.occurredAt.valueOf(),
    );

    const intervals: PauseInterval[] = [];
    let pauseStart: Dayjs | null = null;

    for (const event of sorted) {
      if (event.eventName === JobEventName.JOB_PAUSED && !pauseStart) {
        pauseStart = event.occurredAt;
      } else if (event.eventName === JobEventName.JOB_RESUMED && pauseStart) {
        intervals.push({
          start: pauseStart,
          end: event.occurredAt,
          type: findSegmentTypeBefore(pings, pauseStart),
        });
        pauseStart = null;
      }
    }

    if (pauseStart) {
      intervals.push({
        start: pauseStart,
        end: pauseStart,
        type: findSegmentTypeBefore(pings, pauseStart),
        isOpen: true,
      });
    }

    return intervals;
  }, [events, pings]);

export { usePauseSegments };
